import { useCallback, useMemo, useState } from 'react';
import { LocateFixed, Plus } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { isoDate, type LatLon } from '@findmy/shared';
import { useDevices, useHistory, useSettings } from '../api/queries';
import { DeviceDetail } from '../components/DeviceDetail';
import { DeviceListItem } from '../components/DeviceListItem';
import { HistoryPanel } from '../components/HistoryPanel';
import { ErrorText, Spinner } from '../components/ui';
import { isApproximate, rangeFromParams, rangeInstants, type DayRange } from '../lib/history';
import { useNow } from '../lib/useNow';
import { DeviceMap } from '../map/DeviceMap';
import { HistoryLayer } from '../map/HistoryLayer';

type MyPosition =
  | { kind: 'off' }
  | { kind: 'asking' }
  | { kind: 'on'; position: LatLon }
  | { kind: 'error'; message: string };

export function MapPage() {
  const { data: devices, error, isPending } = useDevices();
  const [params, setParams] = useSearchParams();
  const now = useNow();
  const [me, setMe] = useState<MyPosition>({ kind: 'off' });

  const selectedId = params.get('device');
  const selected = devices?.find((d) => d.id === selectedId) ?? null;
  const select = (id: string | null) => setParams(id ? { device: id } : {}, { replace: false });
  const myPosition = me.kind === 'on' ? me.position : null;

  // History: ?device=…&view=history&from=2026-10-17&to=2026-10-24 (UK days, inclusive).
  const historyDevice = selected && params.get('view') === 'history' ? selected : null;
  const range = rangeFromParams(params.get('from'), params.get('to'), now);
  const { from, to } = rangeInstants(range);
  const showHistory = (device: string, next: DayRange) =>
    setParams({ device, view: 'history', from: isoDate(next.from), to: isoDate(next.to) });
  const history = useHistory(historyDevice?.id ?? null, from, to);
  const { data: settings } = useSettings();
  const [showApproximate, setShowApproximate] = useState(false);
  const points = useMemo(
    () => (history.data?.locations ?? []).filter((l) => showApproximate || !isApproximate(l)),
    [history.data, showApproximate],
  );
  // The timeline starts at (and, as new fixes arrive, stays on) the latest point until
  // you move it. A new range or filter starts again from the latest.
  const viewKey = `${historyDevice?.id}:${from}:${to}:${showApproximate}`;
  const [picked, setPicked] = useState<{ key: string; index: number } | null>(null);
  const index = Math.min(picked?.key === viewKey ? picked.index : points.length - 1, points.length - 1);
  const pick = useCallback((i: number) => setPicked({ key: viewKey, index: i }), [viewKey]);
  const fitKey = history.data && !history.isPlaceholderData ? `${historyDevice?.id}:${from}:${to}` : null;

  // Your own position is used only in this browser, to show distances. It's never sent anywhere.
  const locateMe = () => {
    if (!('geolocation' in navigator)) {
      setMe({ kind: 'error', message: 'This browser can’t share its location.' });
      return;
    }
    setMe({ kind: 'asking' });
    navigator.geolocation.getCurrentPosition(
      (pos) => setMe({ kind: 'on', position: { lat: pos.coords.latitude, lon: pos.coords.longitude } }),
      (err) =>
        setMe({
          kind: 'error',
          message:
            err.code === err.PERMISSION_DENIED
              ? 'Location permission was refused.'
              : 'Couldn’t get your location.',
        }),
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 },
    );
  };

  return (
    <div className="absolute inset-0">
      <DeviceMap devices={devices ?? []} now={now} selectedId={selectedId} onSelect={select} me={myPosition}>
        {historyDevice && (
          <HistoryLayer
            device={historyDevice}
            points={points}
            current={points[index] ?? null}
            fitKey={fitKey}
          />
        )}
      </DeviceMap>

      {/* Floating panel on desktop; bottom sheet on phones. */}
      <aside className="absolute inset-x-0 bottom-0 z-[1000] flex max-h-[48%] flex-col rounded-t-2xl bg-white shadow-2xl ring-1 ring-slate-200 md:inset-x-auto md:top-3 md:bottom-3 md:left-3 md:max-h-none md:w-[22rem] md:rounded-2xl dark:bg-slate-900 dark:ring-slate-800">
        <div
          className="mx-auto mt-2 h-1 w-10 rounded-full bg-slate-300 md:hidden dark:bg-slate-700"
          aria-hidden
        />
        {historyDevice ? (
          <HistoryPanel
            device={historyDevice}
            now={now}
            range={range}
            onRangeChange={(next) => showHistory(historyDevice.id, next)}
            retentionDays={settings?.retentionDays}
            history={history.data}
            loading={history.isFetching}
            error={history.error}
            points={points}
            index={index}
            onIndex={pick}
            showApproximate={showApproximate}
            onShowApproximate={setShowApproximate}
            onBack={() => select(historyDevice.id)}
          />
        ) : selected ? (
          <DeviceDetail
            device={selected}
            now={now}
            me={myPosition}
            onBack={() => select(null)}
            onHistory={() => showHistory(selected.id, rangeFromParams(null, null, now))}
          />
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
              <h1 className="font-semibold">
                Devices {devices && <span className="font-normal text-slate-500">({devices.length})</span>}
              </h1>
              <button
                type="button"
                onClick={locateMe}
                disabled={me.kind === 'asking'}
                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ${me.kind === 'on' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}
                title="Show how far each device is from you. Your position stays in this browser."
              >
                <LocateFixed className="h-4 w-4" aria-hidden />
                {me.kind === 'on'
                  ? 'Using your location'
                  : me.kind === 'asking'
                    ? 'Locating…'
                    : 'Distance from me'}
              </button>
            </div>
            {me.kind === 'error' && (
              <div className="px-4 pb-2">
                <ErrorText>{me.message}</ErrorText>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
              {isPending && (
                <div className="p-4">
                  <Spinner label="Loading devices…" />
                </div>
              )}
              {error && (
                <div className="p-4">
                  <ErrorText>{error.message}</ErrorText>
                </div>
              )}
              {devices?.length === 0 && (
                <div className="space-y-3 p-4 text-sm text-slate-600 dark:text-slate-400">
                  <p>No devices yet. Pair your first one to see it here.</p>
                  <Link
                    to="/devices?pair=1"
                    className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 font-medium text-white hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" aria-hidden /> Add a device
                  </Link>
                </div>
              )}
              <ul aria-label="Devices">
                {devices?.map((device) => (
                  <DeviceListItem
                    key={device.id}
                    device={device}
                    now={now}
                    me={myPosition}
                    onSelect={select}
                  />
                ))}
              </ul>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
